<template>
  <div class="crm-dashboard">
    <b-row class="row-spacing" v-if="isAdmin">
      <b-col cols="12" class="portlet-wrapper">
        <b-card no-body class="portlet">
          <div class="portlet-header">
            <h3 class="text-white mb-0">Global Filters</h3>
          </div>
          <b-card-body class="portlet-body">
            <GlobalFilterBar
              ref="globalFilterBar"
              :base-api="baseApi"
              :access-token="accessToken"
              :show-channel-partner-filter="isAdmin"
              :channel-partner-scope-id="channelPartnerScopeId"
              @filters-changed="handleFiltersChanged"
              @search="handleSearch"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="row-spacing">
      <b-col cols="12" class="portlet-wrapper">
        <b-card no-body class="portlet">
          <div class="portlet-header">
            <h3 class="text-white mb-0">CRM Summary</h3>
          </div>
          <b-card-body class="portlet-body kpi-panel-body">
            <b-alert v-if="fetchError" show variant="danger" class="mb-2">
              {{ fetchError }}
            </b-alert>
            <div v-if="isLoading" class="dashboard-loading text-center py-4">
              <b-spinner variant="primary" label="Loading dashboard" />
              <p class="text-muted mb-0 mt-2">Loading summary…</p>
            </div>
            <b-row v-else class="summary-kpi-row">
              <b-col
                v-for="card in summaryCards"
                :key="card.key"
                cols="12"
                sm="6"
                md="4"
                lg="3"
                xl="2"
                class="summary-kpi-col"
              >
                <div
                  class="summary-kpi-clickable"
                  role="button"
                  tabindex="0"
                  @click="handleSummaryCardClick(card.key)"
                  @keyup.enter="handleSummaryCardClick(card.key)"
                >
                  <kpi-card
                    :title="card.title"
                    :value="card.value"
                    :subtitle="card.subtitle"
                    :color="card.color"
                  />
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <DashboardDetailModal
      :widget-title="detailWidgetTitle"
      :detail-endpoint="detailEndpoint"
      :detail-params="detailParams"
      :filters="dashboardFilters"
      :visible.sync="detailModalVisible"
      @modal-closed="closeDetailModal"
    />
  </div>
</template>

<script>
import { dashboardGet } from '@/components/dashboard/dashboardApi';
import moment from 'moment';
import { BRow, BCol, BCard, BCardBody, BSpinner, BAlert } from 'bootstrap-vue';
import GlobalFilterBar from '@/components/dashboard/GlobalFilterBar.vue';
import KpiCard from '@/components/dashboard/KpiCard.vue';
import DashboardDetailModal from '@/components/dashboard/DashboardDetailModal.vue';

/** CP summary card key → CRM dashboard detail API (same as management dashboard). */
const SUMMARY_CARD_DETAIL_MAP = {
  total_customer: {
    title: 'Total Customer',
    endpoint: '/dashboard/crm/presales-pipeline/detail/total_leads',
  },
  interested: {
    title: 'Interested',
    endpoint: '/dashboard/crm/presales-pipeline/detail/qualified_interested_leads',
  },
  site_visit_interested: {
    title: 'Site Visit Interested',
    endpoint: '/dashboard/crm/presales-pipeline/detail/site_visit_interested',
  },
  site_visit_done: {
    title: 'Site Visit Done',
    endpoint: '/dashboard/crm/sales-pipeline/detail/site_visit_done',
  },
  negotiation: {
    title: 'Negotiation',
    endpoint: '/dashboard/crm/sales-pipeline/detail/negotiation',
  },
  closure: {
    title: 'Closure',
    endpoint: '/dashboard/crm/sales-pipeline/detail/total_closures',
  },
  token: {
    title: 'Token',
    endpoint: '/dashboard/crm/sales-pipeline/detail/token',
  },
  commission: {
    title: 'Commission',
    endpoint: '/dashboard/crm/sales-pipeline/detail/commission',
  },
  closure_meeting: {
    title: 'Closure Meeting',
    endpoint: '/dashboard/crm/sales-pipeline/detail/closure_meeting',
  },
  total_payment_revenue: {
    title: 'Total Payment / Revenue',
    endpoint: '/dashboard/crm/sales-pipeline/detail/total_payment_revenue',
  },
};

const emptyPipeline = () => ({
  total_leads: { count: 0, conversion_rate: null },
  site_visit_interested: { count: 0, conversion_rate: null },
  qualified_interested_leads: { count: 0, conversion_rate: null },
});

const emptySales = () => ({
  negotiation: { count: 0 },
  total_closures: { count: 0 },
  site_visit_done: { count: 0 },
  token: { count: 0 },
  commission: { count: 0 },
  closure_meeting: { count: 0 },
  payment: { count: 0 },
  total_payment_revenue: { value: 0 },
});

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  } catch (e) {
    return {};
  }
}

function isAdminUser(user) {
  const role = String(user?.role || '').toLowerCase();
  const type = String(user?.type || '').toLowerCase();
  return role === 'admin' || type === 'admin';
}

function isChannelPartnerUser(user) {
  const role = String(user?.role || '').toLowerCase();
  const type = String(user?.type || '').toLowerCase();
  return role === 'channelpartner' || type === 'channelpartner';
}

export default {
  name: 'CrmDashboard',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BSpinner,
    BAlert,
    GlobalFilterBar,
    KpiCard,
    DashboardDetailModal,
  },
  data() {
    return {
      baseApi: process.env.VUE_APP_APIENDPOINT,
      accessToken: localStorage.getItem('accessToken'),
      filters: {
        date_from: moment().startOf('month').format('YYYY-MM-DD'),
        date_to: moment().format('YYYY-MM-DD'),
      },
      presalesPipeline: emptyPipeline(),
      salesPipeline: emptySales(),
      isLoading: false,
      initialFetched: false,
      _fetchTimer: null,
      detailModalVisible: false,
      detailWidgetTitle: '',
      detailEndpoint: '',
      detailParams: {},
      fetchError: null,
    };
  },
  computed: {
    isAdmin() {
      return isAdminUser(readUserData());
    },
    isChannelPartner() {
      return isChannelPartnerUser(readUserData());
    },
    channelPartnerScopeId() {
      if (!this.isChannelPartner) {
        return null;
      }
      const cpId = Number(readUserData().id);
      return Number.isFinite(cpId) && cpId > 0 ? cpId : null;
    },
    /** API params: CP login always scopes to their user id (+ downline on server). */
    dashboardFilters() {
      const payload = { ...this.filters };
      if (this.channelPartnerScopeId) {
        payload.channel_partner_id = this.channelPartnerScopeId;
      }
      return payload;
    },
    summaryCards() {
      const presales = this.presalesPipeline;
      const sales = this.salesPipeline;
      const totalCustomers = Number(presales.total_leads?.count) || 0;
      const siteVisitInterested = Number(presales.site_visit_interested?.count) || 0;
      const siteVisitRate = presales.site_visit_interested?.conversion_rate;
      const interested = Number(presales.qualified_interested_leads?.count) || 0;
      const interestedRate = presales.qualified_interested_leads?.conversion_rate;
      const negotiation = Number(sales.negotiation?.count) || 0;
      const closure = Number(sales.total_closures?.count) || 0;
      const token = Number(sales.token?.count) || 0;
      const commission = Number(sales.commission?.count) || 0;
      const siteVisitDone = Number(sales.site_visit_done?.count) || 0;
      const closureMeeting = Number(sales.closure_meeting?.count) || 0;
      const payment = Number(sales.payment?.count) || 0;
      const totalPaymentRevenue = Number(sales.total_payment_revenue?.value) || 0;

      return [
        {
          key: 'total_customer',
          title: 'Total Customer',
          value: this.formatCount(totalCustomers),
          color: 'blue darken-1',
        },
        {
          key: 'site_visit_done',
          title: 'Site Visit Done',
          value: this.formatCount(siteVisitDone),
          color: 'cyan darken-1',
        },
        {
          key: 'negotiation',
          title: 'Negotiation',
          value: this.formatCount(negotiation),
          color: 'amber darken-1',
        },
        {
          key: 'closure',
          title: 'Closure',
          value: this.formatCount(closure),
          color: 'purple darken-1',
        },
        {
          key: 'token',
          title: 'Token',
          value: this.formatCount(token),
          color: 'teal darken-1',
        },
        {
          key: 'commission',
          title: 'Commission',
          value: this.formatCount(commission),
          color: 'blue-grey darken-1',
        },
        {
          key: 'interested',
          title: 'Interested',
          value: this.formatCount(interested),
          color: 'green darken-1',
        },
        {
          key: 'site_visit_interested',
          title: 'Site Visit Interested',
          value: this.formatCount(siteVisitInterested),
          color: 'orange darken-1',
        },
        {
          key: 'closure_meeting',
          title: 'Closure Meeting',
          value: this.formatCount(closureMeeting),
          color: 'light-green darken-1',
        },
        {
          key: 'total_payment_revenue',
          title: 'Total Payment / Revenue',
          value: this.formatCurrency(totalPaymentRevenue),
          color: 'indigo darken-1',
        },
      ];
    },
  },
  mounted() {
    // GlobalFilterBar emits filters on mount; fetch runs from handleFiltersChanged.
    this.$nextTick(() => {
      if (!this.initialFetched) {
        this.initialFetched = true;
        this.fetchSummaryData();
      }
    });
  },
  activated() {
    this.fetchSummaryData();
  },
  beforeDestroy() {
    if (this._fetchTimer) {
      clearTimeout(this._fetchTimer);
    }
  },
  methods: {
    formatCount(value) {
      return Number(value || 0).toLocaleString('en-IN');
    },
    formatCurrency(value) {
      const n = Number(value || 0);
      return `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    },
    formatRateSubtitle(rate, suffix) {
      if (rate === null || rate === undefined || rate === '') {
        return null;
      }
      const n = Number(rate);
      if (Number.isNaN(n)) {
        return null;
      }
      return `${n.toFixed(2)}% ${suffix}`;
    },
    handleFiltersChanged(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      if (!this.initialFetched) {
        this.initialFetched = true;
        this.fetchSummaryData();
        return;
      }
      this.debouncedFetch();
    },
    handleSearch(newFilters) {
      this.filters = { ...this.filters, ...newFilters };
      this.debouncedFetch(true);
    },
    debouncedFetch(forceRefresh = false) {
      if (this._fetchTimer) {
        clearTimeout(this._fetchTimer);
      }
      this._fetchTimer = setTimeout(() => {
        this._fetchTimer = null;
        this.fetchSummaryData(forceRefresh);
      }, 400);
    },
    handleSummaryCardClick(cardKey) {
      const config = SUMMARY_CARD_DETAIL_MAP[cardKey];
      if (!config) {
        return;
      }
      this.openDetailModal(config.title, config.endpoint);
    },
    openDetailModal(widgetTitle, detailEndpoint, detailParams = {}) {
      this.detailWidgetTitle = widgetTitle;
      this.detailEndpoint = detailEndpoint;
      this.detailParams = detailParams;
      this.detailModalVisible = true;
    },
    closeDetailModal() {
      this.detailModalVisible = false;
      this.detailWidgetTitle = '';
      this.detailEndpoint = '';
      this.detailParams = {};
    },
    async fetchSummaryData(forceRefresh = false) {
      if (!this.baseApi || !this.accessToken) {
        return;
      }
      this.isLoading = true;
      this.fetchError = null;
      try {
        const params = { ...this.dashboardFilters };
        if (forceRefresh) {
          params.refresh = 1;
        }
        const response = await dashboardGet(
          this.baseApi,
          this.accessToken,
          '/dashboard/crm/all',
          params
        );
        const data = response.data?.data || {};
        this.presalesPipeline = {
          ...emptyPipeline(),
          ...(data.presales_pipeline || {}),
        };
        this.salesPipeline = {
          ...emptySales(),
          ...(data.sales_pipeline || {}),
        };
      } catch (error) {
        console.error('Error fetching CRM summary:', error);
        this.fetchError =
          error.response?.data?.message || error.message || 'Failed to load dashboard summary';
        this.presalesPipeline = emptyPipeline();
        this.salesPipeline = emptySales();
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.crm-dashboard {
  padding: 20px;
}

.row-spacing {
  margin-top: 20px;
  margin-bottom: 20px;
}

.portlet {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.portlet:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
}

.portlet-header {
  padding: 10px 16px;
  background: linear-gradient(135deg, #4e73df, #224abe);
  color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.portlet-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.portlet-body {
  padding: 20px;
  flex-grow: 1;
}

.kpi-panel-body {
  padding: 8px 12px 16px;
}

.summary-kpi-row {
  margin-left: -6px;
  margin-right: -6px;
}

.summary-kpi-col {
  padding-left: 6px;
  padding-right: 6px;
  margin-bottom: 12px;
}

.summary-kpi-clickable {
  cursor: pointer;
  height: 100%;
}

.summary-kpi-clickable:focus {
  outline: 2px solid #4e73df;
  outline-offset: 2px;
  border-radius: 8px;
}
</style>
